import { createFileRoute } from "@tanstack/react-router"
import { lazy, Suspense, useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { TripPage } from "@/components/pages/TripPage/lazy"
import { NotFound } from "@/components/NotFound"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import { fetchGuideMeta } from "@/lib/guide-meta"
import { fetchGuideSnapshotJson } from "@/lib/guide-snapshot"
import { guideSnapshotKey } from "@/lib/hooks/use-guide-data"
import { fetchViewportHint } from "@/lib/viewport-hint"
import { ViewportHintProvider } from "@/lib/contexts/viewport-hint-context"
import { defaultMeta, noIndex, absoluteUrl } from "@/lib/seo"
import { guideSlug } from "@/lib/slugify"
import { useCanonicalId } from "@/lib/hooks/use-canonical-id"

// HeroUI's ~249 KB barrel, loaded lazily and ONLY around the interactive
// TripPage on the logged-out path (the authed layout branch already mounts it).
const LazyHeroProvider = lazy(() => import(`@/components/LazyHeroProvider`))

function GuidePageRoute() {
  const { guideId: param } = Route.useParams()
  const { copy } = Route.useSearch()
  const { snapshot, wide } = Route.useLoaderData()
  // New links carry a short id, old links a uuid — everything below the route
  // (cache keys, broadcast channel, recordView) needs the canonical uuid. The
  // loader already resolved it for a published guide, which is what lets the
  // server render the guide view; otherwise resolve it in the browser.
  const resolvedId = useCanonicalId(`guide`, param)
  const guideId = snapshot?.id ?? resolvedId
  const { data: session } = authClient.useSession()

  // Seed the guide view's snapshot query with the loader's data BEFORE TripPage
  // renders, so it renders the real guide on the server (crawlable, and no
  // stand-in page or spinner before it) and the browser's first render — fed
  // the same dehydrated loader data — matches it. Seeded once, and never over
  // fresher cache data (a client-side revisit keeps its own query).
  const queryClient = useQueryClient()
  useState(() => {
    if (!snapshot) return
    const key = guideSnapshotKey(snapshot.id)
    if (queryClient.getQueryData(key) === undefined) {
      queryClient.setQueryData(key, JSON.parse(snapshot.json))
    }
  })

  useEffect(() => {
    if (!guideId) return
    trpc.guides.recordView.mutate({ id: guideId }).catch(() => {})
  }, [guideId])

  if (guideId === undefined) return null
  if (guideId === null) return <NotFound />

  // TripPage (read-only guide mode) reads the snapshot seeded above and renders
  // the guide view. TripPage needs HeroUI: under the authed layout branch that
  // provider is already mounted, so wrap only on the logged-out path — a second
  // ToastProvider would double every toast. A ?copy=1 resume (a copy
  // interrupted by the login wall) completes when TripPage mounts.
  const planner = (
    <TripPage guideId={guideId} readOnly={true} autoCopy={copy === `1`} />
  )
  // `wide` is the server's desktop/phone guess, so the guide view's server
  // render picks the same layout the browser will (see viewport-hint.ts).
  return (
    <ViewportHintProvider value={wide}>
      <Suspense fallback={null}>
        {session?.user ? planner : <LazyHeroProvider>{planner}</LazyHeroProvider>}
      </Suspense>
    </ViewportHintProvider>
  )
}

export const Route = createFileRoute(
  `/_with-optional-sidebar/guide/$slug/$guideId/`
)({
  // ?copy=1 marks a copy interrupted by the login wall — /login sends the
  // signed-in user back here with it set, and TripPage resumes the copy.
  validateSearch: (search: Record<string, unknown>) => ({
    copy: search.copy === `1` || search.copy === 1 ? (`1` as const) : undefined,
  }),
  // Server reads — the data ones direct-Postgres server fns (NEVER a self-fetch to our
  // own /api/trpc — that deadlocks inside the render; see guide-meta.ts):
  //  - fetchGuideMeta → head()'s title/description/canonical/og:image + the
  //    published flag that decides robots.
  //  - fetchViewportHint → a desktop/phone guess so that server render picks
  //    the right layout (see viewport-hint.ts).
  //  - fetchGuideSnapshotJson → the published guide's data, seeded into the
  //    guide view's query so the guide view itself renders on the server (null
  //    for unpublished / unknown: the browser fetches, or it's a 404).
  //
  // Must NEVER throw: this loader runs server-side under full SSR, and a thrown
  // loader renders TanStack's default error boundary ("Something went wrong!")
  // as the indexed HTML body. So both fetches are caught here. Crucially we keep
  // fetchGuideMeta's two failure modes DISTINCT: it returns `{ guide: null }`
  // for a genuine not-found but THROWS on a transient DB error. Collapsing both
  // to `guide: null` would let a DB blip during a crawl serve `noindex` on a
  // REAL published guide — the exact incident that once deindexed the library.
  // So a thrown meta becomes `undefined` (transient) and is flagged separately
  // from a null guide (genuine 404).
  loader: async ({ params }) => {
    const [meta, snapshot, wide] = await Promise.all([
      fetchGuideMeta({ data: { id: params.guideId } }).catch(() => undefined),
      fetchGuideSnapshotJson({ data: { id: params.guideId } }).catch(() => null),
      fetchViewportHint().catch(() => true),
    ])
    return {
      guide: meta?.guide ?? null,
      snapshot,
      wide,
      transientError: meta === undefined,
    }
  },
  head: ({ params, loaderData }) => {
    // Belt-and-suspenders for the bare shell render where the loader hasn't run:
    // neutral, indexable meta with a self-canonical, never `noindex` — a raw-HTML
    // noindex that Google honors without rendering once deindexed the whole
    // guide library (regression-locked in index.test.ts).
    if (loaderData === undefined) {
      return defaultMeta({ path: `/guide/${params.slug}/${params.guideId}` })
    }
    // Transient DB error during the crawl: the guide may well be real and
    // published, so we must NOT noindex it. Emit neutral indexable meta with a
    // self-canonical and bail before the !guide noindex branch below.
    if (loaderData.transientError) {
      return defaultMeta({ path: `/guide/${params.slug}/${params.guideId}` })
    }
    const guide = loaderData.guide
    // Loader ran and found nothing: the page still renders a 200, so without
    // this it would inherit indexable meta — a soft 404. Keep it out of the
    // index instead. No JSON-LD for a guide that isn't there.
    if (!guide) return defaultMeta({ path: `/guide`, robots: noIndex })
    const title = guide.name || `${guide.destination} travel guide`
    // No <link rel=preload> for the cover: the server-rendered guide view's
    // hero (GuideHero) emits the banner <img> (responsive src/srcSet), so the browser's preload scanner fetches it
    // during the initial HTML parse — before any JS. A head preload of the raw
    // image_url would just mismatch the srcSet-selected variant and waste bytes.
    const description =
      guide.description ||
      `A travel guide for ${guide.destination}, built on ChicTravel.`
    // Canonicalize on the short-id URL: old uuid links stay reachable but point
    // crawlers at the one canonical form (no duplicate content).
    const path = `/guide/${guideSlug(guide)}/${guide.short_id}`
    const canonical = absoluteUrl(path)
    const base = defaultMeta({
      title,
      description,
      path,
      image: guide.image_url ?? undefined,
      ogType: `article`,
      // Don't let crawlers index unpublished/empty guides reachable by URL.
      robots: guide.published ? undefined : noIndex,
    })
    // Richer than the old bare TouristDestination: a TouristTrip with geo +
    // image so the guide reads as a real itinerary in structured data. Emitted
    // from head() (SSR) so it lands in the raw HTML — supersedes the old
    // client-only JSON-LD in PublicTripHelmet (now removed). Only on a real,
    // published guide; geo only when we actually have coordinates.
    const jsonLd: Record<string, unknown> = {
      "@context": `https://schema.org`,
      "@type": `TouristTrip`,
      name: title,
      description,
      url: canonical,
      ...(guide.image_url ? { image: guide.image_url } : {}),
      ...(guide.destination_latitude != null &&
      guide.destination_longitude != null
        ? {
            geo: {
              "@type": `GeoCoordinates`,
              latitude: guide.destination_latitude,
              longitude: guide.destination_longitude,
            },
          }
        : {}),
    }
    return {
      ...base,
      scripts: [
        { type: `application/ld+json`, children: JSON.stringify(jsonLd) },
      ],
    }
  },
  component: GuidePageRoute,
  // Full SSR: the loader + head() + the guide view (TripPage, seeded from the
  // loader) all render on the server, so crawlers get the real guide in raw
  // HTML and visitors see the guide view from the first paint. Both ancestor
  // gates (rootSsr + the layout's optionalSidebarSsr) also return true
  // for this URL — a leaf can't SSR if any ancestor is ssr:false.
  ssr: true,
})
